import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Navigate, useParams } from 'react-router';
import { useTypedSelector } from '../reducers';
import { ScreenWrap, HeaderBreadcrumbs, SoloLoading, ScreenError } from '.';
import { Api } from '../api';
import { Check, X } from '@phosphor-icons/react';
import {
  addDays,
  format,
  isAfter,
  parseISO,
  isBefore,
  startOfDay,
} from 'date-fns';
import { PieChart } from '@mui/x-charts/PieChart';
import { Model } from '../models';

/** Weeks the member has reached in one program, newest first. */
function reachedWeeks(program: Api.UserProgram.V): number {
  if (!program.progress) return 0;
  return program.progress.status === 'finished'
    ? program.totalWeeks
    : program.progress.currentWeekNumber || 0;
}

export default function ProgramProgressScreen() {
  const { id, programId } = useParams();
  const { viewMode } = useTypedSelector((state) => state.auth);
  const statsApi = Api.UserStats.useReadItemStats(id || '');
  const programsApi = Api.UserProgram.useReadCollection(id || '');
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const program = programsApi.data?.member.find((p) => p.id === programId);

  useEffect(() => {
    if (selectedWeek || !program) return;
    const week = reachedWeeks(program);
    if (week) {
      setSelectedWeek(String(week));
    }
  }, [program, selectedWeek]);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  if (statsApi.error) return <ScreenError {...statsApi} />;
  if (programsApi.error) return <ScreenError {...programsApi} />;
  if (!statsApi.data || !programsApi.data) return <SoloLoading />;

  // Old links (/users/:id/program) open the latest started program
  if (!programId) {
    const started = programsApi.data.member.filter((p) => p.participationId);
    const latest = started[started.length - 1];
    return latest ? (
      <Navigate
        replace
        to={`/users/${id}/program/${latest.id}${window.location.hash}`}
      />
    ) : (
      <Navigate replace to={`/users/${id}/user-dashboard`} />
    );
  }
  if (!program) return <SoloLoading />;

  const currentWeek = reachedWeeks(program);
  const isAdmin = viewMode === 'admin';

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedWeek(newValue);
  };

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <Box>
          <HeaderBreadcrumbs
            title="Program Progress"
            items={[
              {
                name: isAdmin ? 'App Users' : 'Your Clients',
                url: '/users',
              },
              {
                name: statsApi.data?.user.name || 'User',
                url: `/users/${id}/user-dashboard`,
              },
              {
                name: 'Program Progress',
              },
              {
                name: program.title,
              },
            ]}
          />
        </Box>
        <Tabs
          value={selectedWeek}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {Array.from({ length: currentWeek }, (_, i) => currentWeek - i).map(
            (w) => (
              <Tab key={w} value={String(w)} label={`Week ${w}`} />
            ),
          )}
        </Tabs>

        <ProgramView
          userId={id || ''}
          programId={program.id}
          selectedWeek={selectedWeek || '1'}
        />
      </Stack>
    </ScreenWrap>
  );
}

function ProgramView({
  userId,
  programId,
  selectedWeek,
}: {
  userId: string;
  programId: string;
  selectedWeek: string;
}) {
  const weekApi = Api.ProgramWeek.useReadItemByProgram(
    userId,
    programId,
    selectedWeek,
  );

  if (weekApi.error) return <ScreenError {...weekApi} />;
  if (!weekApi.data) return <SoloLoading />;
  const programWeek = weekApi.data;

  // The member's own dates for the week, so a pause or frozen stretch doesn't shift the days
  const today = new Date();
  const weekStartDate = programWeek.startDate
    ? parseISO(programWeek.startDate)
    : startOfDay(today);
  const weekEndDate = programWeek.endDate
    ? parseISO(programWeek.endDate)
    : addDays(weekStartDate, 6);

  const dayNames = Array.from({ length: 7 }, (_, i) =>
    format(addDays(weekStartDate, i), 'EEE'),
  );

  const normalizeDate = (d: Date | string) =>
    format(typeof d === 'string' ? parseISO(d) : d, 'yyyy-MM-dd');

  const answeredReflectionPoints = programWeek.reflectionPoints.filter(
    (rp) => rp.userAnswer?.answer,
  );

  return (
    <Stack spacing={3}>
      <Card>
        <CardHeader title={<Typography variant="h6">Daily Tips</Typography>} />
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              {(() => {
                const viewedCount = programWeek.dailyTips.filter(
                  (t) => t.isSaved || t.isViewed,
                ).length;
                const total = programWeek.dailyTips.length;
                return (
                  <>
                    <PieChart
                      series={[
                        {
                          data: [
                            { id: 0, value: viewedCount, color: '#2D556C' },
                            {
                              id: 1,
                              value: Math.max(total - viewedCount, 0),
                              color: '#BDC7CC',
                            },
                          ],
                          innerRadius: 60,
                          outerRadius: 80,
                          paddingAngle: 0,
                          cornerRadius: 0,
                        },
                      ]}
                      width={160}
                      height={160}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h6">
                        {viewedCount}/{total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        viewed
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>

            <Box
              sx={{
                bgcolor: 'var(--mui-palette-background-level1)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Viewed Tips
              </Typography>
              <Stack direction="row" spacing={2}>
                {dayNames.map((name, index) => {
                  const tip = programWeek.dailyTips.find(
                    (t) => t.day === index + 1,
                  );
                  const viewed = tip?.isViewed;
                  const dayDate = addDays(weekStartDate, index);
                  const isPast = isBefore(dayDate, startOfDay(today));
                  const missed = !viewed && isPast;

                  const textColor = viewed
                    ? 'success.main'
                    : missed
                      ? 'error.main'
                      : 'text.secondary';

                  return (
                    <Stack key={index} alignItems="center" spacing={1}>
                      <Typography variant="body2" color={textColor}>
                        {name}
                      </Typography>
                      {viewed ? (
                        <Check
                          size={16}
                          color="var(--mui-palette-success-main)"
                        />
                      ) : missed ? (
                        <X size={16} color="var(--mui-palette-error-main)" />
                      ) : (
                        <Box sx={{ width: 16, height: 16 }} />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<Typography variant="h6">Tasks of the week</Typography>}
        />
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{ display: 'flex', flexWrap: 'wrap' }}
          >
            {programWeek.tasks.map((task) => {
              const completedSet = new Set(
                (task.completedDates || []).map((d: string) =>
                  normalizeDate(d),
                ),
              );
              const viewed = task.isViewed;
              return (
                <Box
                  key={task['@id']}
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                    flex: 1,
                    minWidth: 280,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {task.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      {dayNames.map((name, i) => {
                        const d = addDays(weekStartDate, i);
                        const key = normalizeDate(d);
                        const isCompleted = completedSet.has(key);
                        const isPast = isBefore(d, startOfDay(today));
                        const status = isCompleted
                          ? 'completed'
                          : isPast
                            ? 'failed'
                            : '';
                        return (
                          <Stack
                            key={i}
                            alignItems="center"
                            spacing={0.5}
                            sx={{ flex: 1 }}
                          >
                            <Typography
                              variant="subtitle2"
                              color={
                                status === 'completed'
                                  ? 'success.main'
                                  : status === 'failed'
                                    ? 'error.main'
                                    : 'text.secondary'
                              }
                            >
                              {name}
                            </Typography>
                            {status === 'completed' ? (
                              <Check
                                size={16}
                                color="var(--mui-palette-success-main)"
                              />
                            ) : status === 'failed' ? (
                              <X
                                size={16}
                                color="var(--mui-palette-error-main)"
                              />
                            ) : (
                              <Box sx={{ width: 16, height: 16 }} />
                            )}
                          </Stack>
                        );
                      })}
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Resource: {task.resourceTitle}
                      </Typography>
                      <Chip
                        label={viewed ? 'Viewed' : 'Not viewed'}
                        color={viewed ? 'success' : 'default'}
                      />
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card id="psychology-prompts">
        <CardHeader
          title={<Typography variant="h6">Psychology Prompt</Typography>}
        />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 2,
              maxWidth: '100%',
              '@media (min-width: 900px)': {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            {programWeek.prompts.map((item, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: 'var(--mui-palette-background-level1)',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2">{item.title}</Typography>
                  {item.userAnswer?.answer ? (
                    <Typography variant="body2" color="text.secondary">
                      {item.userAnswer?.answer}
                    </Typography>
                  ) : (
                    <Box>
                      <Chip label="Not answered" color="default" />
                    </Box>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card id="reflection-points">
        <CardHeader
          title={<Typography variant="h6">Reflection Points</Typography>}
        />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 2,
              maxWidth: '100%',
              '@media (min-width: 900px)': {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            {isAfter(weekEndDate, today) &&
            answeredReflectionPoints.length === 0 ? (
              <Box
                sx={{
                  bgcolor: 'var(--mui-palette-background-level1)',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  not visible to the user yet, appears at the end of the week
                </Typography>
              </Box>
            ) : (
              programWeek.reflectionPoints.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">{item.title}</Typography>
                    {item.userAnswer?.answer ? (
                      <Typography variant="body2" color="text.secondary">
                        {item.userAnswer?.answer}
                      </Typography>
                    ) : (
                      <Box>
                        <Chip label="Not answered" color="default" />
                      </Box>
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Box>
        </CardContent>
      </Card>

      <Card id="lessons">
        <CardHeader title={<Typography variant="h6">Lessons</Typography>} />
        <CardContent sx={{ pt: 0 }}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: 1,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>
                    <Typography variant="body2">Title</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    <Typography variant="body2">State</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '25%' }}>
                    <Typography variant="body2">Question</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '30%' }}>
                    <Typography variant="body2">User Reply</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programWeek.lessons.map((row, index) => {
                  const question = row.slides
                    .map((slide) => {
                      const data = Model.Lesson.parseSlideTemplateData(
                        slide.templateName,
                        slide.templateData as any,
                      );
                      return data.question;
                    })
                    .find((question) => question);

                  return (
                    <TableRow key={row['@id'] || index}>
                      <TableCell component="th" scope="row">
                        <Typography variant="body2">{row.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.isCompleted ? 'Completed' : 'To Do'}
                          color={row.isCompleted ? 'success' : 'default'}
                        />
                        {row.myEngagement?.completedAt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {format(
                              parseISO(row.myEngagement.completedAt),
                              'd MMM yyyy',
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {question || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {row.userAnswer?.answer || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
}
